import React, { FC, useMemo } from "react";
import LmsTable from "../ui/LmsTable";
import { useFetchData, useParseRouteActions } from "../../../helpers/GlobalHelper";
import LmsFormGenerator, {
  BCFormController,
  IBCFormControllerOptions,
} from "../ui/LmsFormGenerator";
import { LmsEndpoints } from "../../constants/LmsEndpoints";
import { GlobalConstants } from "../../constants/GlobalConstants";

const INSURANCE_META_ENDPOINT = "metadata/insurance-metadata";
type FieldGetterFn = (myForm: BCFormController) => any;
type FieldSetterFn = (myForm: BCFormController) => BCFormController;
type ParamsType = Record<string, null | string>;

enum CoverageTypeId {
  Blanket = "c2b26204-803c-47a5-85c9-a65100c84687",
  Property = "5e2b0558-ba65-4bf2-9ded-a6b80029cb95",
}

enum InsuranceFormAction {
  ADD = "add",
  EDIT = "edit",
  VIEW = "view",
}

enum InsuranceFormName {
  ORG = "org",
  PROPERTY = "property",
}

export const INSURANCE_BASE_PATH = "/servicing-insurance";
export const INSURANCE_PATHS = {
  ADD_ORG: `${InsuranceFormAction.ADD}-${InsuranceFormName.ORG}`,
  EDIT_ORG: `${InsuranceFormAction.EDIT}-${InsuranceFormName.ORG}`,
  ADD_PROPERTY: `${InsuranceFormAction.ADD}-${InsuranceFormName.PROPERTY}`,
  EDIT_PROPERTY: `${InsuranceFormAction.EDIT}-${InsuranceFormName.PROPERTY}`,
  VIEW: InsuranceFormAction.VIEW,
};

export enum InsuranceParams {
  PROPERTY = "loanPropertyId",
}

interface InsuranceFormProps {
  id: string;
  params: ParamsType;
  action: string;
}

const insurancePathValues = Object.values(INSURANCE_PATHS);

const fieldSetter =
  (fieldGetters: (FieldGetterFn | FieldGetterFn[])[]): FieldSetterFn =>
  (myForm) => {
    fieldGetters.forEach((fieldGetter: FieldGetterFn | FieldGetterFn[]) => {
      const isArray = Array.isArray(fieldGetter);
      myForm.addCenteredGroup(
        isArray ? fieldGetter.map((getter) => getter(myForm)) : [fieldGetter(myForm)],
        {
          isHiddenFunc: (Array.isArray(fieldGetter) ? fieldGetter[0] : fieldGetter)(myForm)
            .isHiddenFunc,
        }
      );
    });
    return myForm;
  };

const isLoanHidden = ({ inputs }) => inputs.policyCoverageId !== CoverageTypeId.Property;

const isLoanDisabled = ({ inputs }) => inputs.loanDisabled;

const getLoanPropertyField: FieldGetterFn = (myForm) =>
  myForm.getDropdownField("Loan Property", "loans/loan-properties", {
    dependencyField: "loanId",
    isHiddenFunc: isLoanHidden,
    isDisabledOnFunc: ({ inputs }) => inputs.propertyDisabled,
  });

const getApplicationNumberField: FieldGetterFn = (myForm) =>
  myForm.getDisplayField("Application #", {
    name: "applicationNumber",
    isDisabledOnFunc: isLoanDisabled,
    isHiddenFunc: isLoanHidden,
  });

const getBorrowerField: FieldGetterFn = (myForm) =>
  myForm.getDisplayField("Borrower", { name: "organizationDisplay" });

const getOrganizationField: FieldGetterFn = (myForm) =>
  myForm.getDropdownField("Organization", LmsEndpoints.Orgs.GET.LIST_ORGS, { disabled: true });

const getSpecialPurposeEntityField: FieldGetterFn = (myForm) =>
  myForm.getDropdownField("Special Purpose Entity", LmsEndpoints.Orgs.GET.LIST_SPES, {
    isDisabledOnFunc: ({ inputs }) => inputs.policyCoverageId === CoverageTypeId.Property,
  });

const getLoanField: FieldGetterFn = (myForm) =>
  myForm.getDisplayField("Loan", {
    name: "loanNumber",
    isHiddenFunc: isLoanHidden,
    isDisabledOnFunc: isLoanDisabled,
  });

const getCoverageTypeField: FieldGetterFn = (myForm) =>
  myForm.getDropdownField("Coverage Type", INSURANCE_META_ENDPOINT, {
    dropdownOptionsSubPath: "coverageTypes",
    name: "policyCoverageId",
    disabled: true,
  });

const getPolicyTypeField: FieldGetterFn = (myForm) =>
  myForm.getDropdownField("Policy Type", INSURANCE_META_ENDPOINT, {
    dropdownOptionsSubPath: "policyTypes",
    required: true,
  });

const getProviderField: FieldGetterFn = (myForm) =>
  myForm.getDropdownField("Provider", INSURANCE_META_ENDPOINT, {
    dropdownOptionsSubPath: "providers",
    name: "insuranceBrokerOrganizationId",
    required: true,
  });

const getProviderAgentField: FieldGetterFn = (myForm) =>
  myForm.getDropdownField("Provider Agent", "metadata/list-provider-agents", {
    name: "insuranceBrokerOrganizationContactId",
    dependencyField: "insuranceBrokerOrganizationId",
    required: true,
  });

const getPolicyNumberField: FieldGetterFn = (myForm) =>
  myForm.getInputField("Policy #", { name: "policyNumber", required: true });

const getExpirationDateField: FieldGetterFn = (myForm) =>
  myForm.getCalendarField("Expiration Date", {
    name: "expiryDate",
    required: true,
  });

const isHiddenNotLiability = ({ inputs }) =>
  inputs.policyTypeId !== GlobalConstants.InsuranceMetadata.PolicyTypes.Liability;

const isHiddenLiability = ({ inputs }) =>
  inputs.policyTypeId === GlobalConstants.InsuranceMetadata.PolicyTypes.Liability;

const getCoverageAmountField: FieldGetterFn = (myForm) =>
  myForm.getCurrencyField("Coverage Amount", {
    required: true,
    isHiddenFunc: isHiddenLiability,
  });

const getCoverageAmountPerOccurenceField: FieldGetterFn = (myForm) =>
  myForm.getCurrencyField("Coverage Amount Per Occurence", {
    required: true,
    isHiddenFunc: isHiddenNotLiability,
  });

const getCoverageAmountAggregateField: FieldGetterFn = (myForm) =>
  myForm.getCurrencyField("Coverage Amount Aggregate", {
    required: true,
    isHiddenFunc: isHiddenNotLiability,
  });

const sharedFields = [
  getPolicyTypeField,
  getProviderField,
  getProviderAgentField,
  getPolicyNumberField,
  getExpirationDateField,
  getCoverageAmountField,
  getCoverageAmountPerOccurenceField,
  getCoverageAmountAggregateField,
];

const insurancePropertyFields: FieldSetterFn = fieldSetter([
  getLoanPropertyField,
  getBorrowerField,
  getCoverageTypeField,
  getSpecialPurposeEntityField,
  [getApplicationNumberField, getLoanField],
  ...sharedFields,
]);

const insuranceSPEFields: FieldSetterFn = fieldSetter([
  getOrganizationField,
  getSpecialPurposeEntityField,
  ...sharedFields,
]);

const insuranceOrganizationFields: FieldSetterFn = fieldSetter([
  getOrganizationField,
  getCoverageTypeField,
  [getApplicationNumberField, getLoanField],
  getSpecialPurposeEntityField,
  ...sharedFields,
]);

const getFormOptions = (
  formAction: InsuranceFormAction,
  formName: InsuranceFormName,
  data
): [Record<string, unknown>, IBCFormControllerOptions] => {
  if (data !== null) {
    switch (formName) {
      case InsuranceFormName.PROPERTY: {
        if (formAction === InsuranceFormAction.ADD) {
          return [
            {
              loanNumber: data.loanNumberDisplay,
              organizationDisplay: data.organizationDisplay,
              loanPropertyDisplay: data.loanPropertyDisplay,
              specialPurposeEntity: data.specialPurposeEntity,
              applicationNumber: data.applicationNumberDisplay,
              policyCoverageId: CoverageTypeId.Property,
            },
            {
              submitSuffix: "loanproperty/insurances",
              values: {
                loanPropertyId: data.loanPropertyId,
                organizationId: data.organizationId,
                specialPurposeEntityId: data.specialPurposeEntityId,
                loanId: data.loanId,
                propertyDisabled: true,
                loanDisabled: true,
              },
            },
          ];
        } else if (formAction === InsuranceFormAction.EDIT) {
          return [
            {
              ...data,
              loanNumber: data.loanNumberDisplay,
              applicationNumber: data.applicationNumberDisplay,
            },
            {
              submitSuffix: "loanproperty/insurances",
              deleteSuffix: "loanproperty/insurances",
              clearSuffixesOnSubmit: [`loanproperty/insurances/${data.id}`],
              values: {
                loanId: data.loanId,
                loanPropertyId: data.loanPropertyId,
                propertyDisabled: true,
                loanDisabled: true,
              },
            },
          ];
        }
        break;
      }
      case InsuranceFormName.ORG: {
        if (formAction === InsuranceFormAction.EDIT) {
          return [
            data,
            {
              deleteSuffix: "orgs/insurances",
              submitSuffix: LmsEndpoints.Orgs.PUT.UPDATE_INSURANCE,
              clearSuffixesOnSubmit: [`orgs/insurance/${data.id}`],
            },
          ];
        }
      }
    }
    if (formAction === InsuranceFormAction.VIEW) {
      return [
        {
          ...data,
          loanNumber: data.loanNumberDisplay,
          applicationNumber: data.applicationNumberDisplay,
        },
        { initIsDisabled: true, values: data },
      ];
    }
  }

  return [data || {}, {}];
};

const getDataEndpoint = (
  formAction: InsuranceFormAction,
  formName: InsuranceFormName,
  id: string,
  params: ParamsType
) => {
  switch (formName) {
    case InsuranceFormName.PROPERTY:
      if (formAction === InsuranceFormAction.ADD && params[InsuranceParams.PROPERTY]) {
        return `loanproperty/insurances/initial-data/${params[InsuranceParams.PROPERTY]}`;
      }
      break;
    case InsuranceFormName.ORG: {
      if (formAction === InsuranceFormAction.EDIT) {
        return `orgs/insurance/${id}`;
      }
    }
  }
  return id ? `loanproperty/insurances/${id}` : "";
};

const InsuranceForm: FC<InsuranceFormProps> = ({ id, action, params }) => {
  const isViewAction = action === "view";
  const [formAction, formName] = (isViewAction ? [action, ""] : action.split("-")) as [
    InsuranceFormAction,
    InsuranceFormName
  ];

  const [{ data = null, isLoading, error }] = useFetchData(
    getDataEndpoint(formAction, formName, id, params)
  );

  const isDataLoaded = data !== null;
  const myForm = useMemo((): BCFormController => {
    const isViewDataLoaded = isDataLoaded && isViewAction;
    let fieldSetter: FieldSetterFn = (myForm) => myForm;
    if (formName === InsuranceFormName.PROPERTY || (isViewDataLoaded && data.loanPropertyId)) {
      fieldSetter = insurancePropertyFields;
    } else if (isViewDataLoaded && data.specialPurposeEntityId) {
      fieldSetter = insuranceSPEFields;
    } else if (formName === InsuranceFormName.ORG || (isViewDataLoaded && data.organizationId)) {
      fieldSetter = insuranceOrganizationFields;
    }

    return fieldSetter(new BCFormController(...getFormOptions(formAction, formName, data)));
  }, [isDataLoaded]);

  return (
    <LmsFormGenerator
      error={error}
      myForm={myForm}
      isLoading={isLoading}
      actions={
        isViewAction
          ? ["close"]
          : formAction === InsuranceFormAction.ADD
          ? ["save-new", "save-close", "close"]
          : ["save", "save-close", "delete", "close"]
      }
    />
  );
};

const InsurancesTable = () => (
  <LmsTable
    apiSuffix="insurances"
    actionButtons={["export"]}
    sortable={["Policy Type"]}
    columnClickOptions={{
      policyType: {
        path: `${INSURANCE_BASE_PATH}/${INSURANCE_PATHS.VIEW}`,
        idProp: "id",
      },
    }}
    columnLabelsDictionary={{
      insuranceBrokerOrganization: "Provider",
      insuranceBrokerOrganizationContact: "Provider Agent",
      policyCoverage: "Coverage type",
      policyNumber: "Policy #",
    }}
    rowsBackgroundCustomColor={(rowData) => rowData?.expiryDateColorId}
  />
);

const LmsInsurance = ({ params = {}, history }) => {
  const { isMainPage, isActionAllowed, id, query, actionLower } = useParseRouteActions(
    params,
    insurancePathValues
  );

  const queryParams = [InsuranceParams.PROPERTY].reduce(
    (acc, param) => ({ ...acc, [param]: query.get(param) }),
    {}
  );

  if (!isActionAllowed) {
    history.goBack();
  }

  return (
    <>
      {isMainPage && <InsurancesTable />}
      {insurancePathValues.includes(actionLower) && (
        <InsuranceForm action={actionLower} id={id} params={queryParams} />
      )}
    </>
  );
};

export default LmsInsurance;
